import { Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AccountGroup, AccountGroupService } from '@galaxy/account-group';
import { TranslateModule } from '@ngx-translate/core';
import { AccountGroupSdkService } from '@vendasta/account-group';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { AccountService } from '../../core/account.service';
import { User } from '../../core/user';

@Component({
  imports: [MatDialogModule, TranslateModule, MatProgressSpinnerModule, MatButtonModule],
  template: `
    <h2 mat-dialog-title>{{ 'MANAGE_ACCOUNTS.DELETE_ACCOUNT_DIALOG.DELETE_ACCOUNT' | translate }}</h2>
    <mat-dialog-content>
      <p
        [innerHtml]="
          'MANAGE_ACCOUNTS.DELETE_ACCOUNT_DIALOG.ARE_YOU_SURE'
            | translate: { accountName: accountGroup.napData.companyName }
        "
      ></p>
      <p>{{ 'MANAGE_ACCOUNTS.DELETE_ACCOUNT_DIALOG.WHAT_HAPPENS' | translate }}</p>
      <ul>
        <li>{{ 'MANAGE_ACCOUNTS.DELETE_ACCOUNT_DIALOG.PRODUCTS_WILL_BE_REMOVED' | translate }}</li>
        <li>{{ 'MANAGE_ACCOUNTS.DELETE_ACCOUNT_DIALOG.COMPANY_WILL_BE_DELETED' | translate }}</li>
        <li>{{ 'MANAGE_ACCOUNTS.DELETE_ACCOUNT_DIALOG.PRODUCTS_WILL_BE_BILLED' | translate }}</li>
      </ul>
      <div>
        @if (loading) {
          <mat-progress-spinner
            class="loading"
            mode="indeterminate"
            strokeWidth="4"
            diameter="40"
          ></mat-progress-spinner>
        }
        @if (unassociatedUsers.length > 0 && !loading) {
          <div>
            <p>
              {{ 'MANAGE_ACCOUNTS.DELETE_ACCOUNT_DIALOG.USERS_WILL_LOSE_ACCESS' | translate }}
            </p>
            <ul>
              @for (user of unassociatedUsers; track user) {
                <li>
                  {{ user.descriptiveName }}
                </li>
              }
            </ul>
          </div>
        }
        @if (orphanedUsers.length > 0 && !loading) {
          <div>
            <p>
              {{ 'MANAGE_ACCOUNTS.DELETE_ACCOUNT_DIALOG.USERS_WILL_BE_DISSOCIATED' | translate }}
            </p>
            <ul>
              @for (user of orphanedUsers; track user) {
                <li>
                  {{ user.descriptiveName }}
                </li>
              }
            </ul>
          </div>
        }
      </div>
    </mat-dialog-content>
    <mat-dialog-actions>
      <button mat-button matDialogClose>{{ 'COMMON.ACTION_LABELS.CANCEL' | translate }}</button>
      <button mat-raised-button color="warn" [disabled]="deleteLoading" (click)="deleteAccount()">
        {{ 'COMMON.ACTION_LABELS.DELETE' | translate }}
      </button>
    </mat-dialog-actions>
    `,
})
export class DeleteAccountDialogComponent implements OnInit {
  accountGroup: AccountGroup;

  loading = true;
  deleteLoading = false;

  unassociatedUsers: User[] = [];
  orphanedUsers: User[] = [];

  constructor(
    public dialogRef: MatDialogRef<DeleteAccountDialogComponent>,
    private snackbarService: SnackbarService,
    private accountService: AccountService,
    private accountGroupService: AccountGroupService,
    private accountGroupSdkService: AccountGroupSdkService,
    @Inject(MAT_DIALOG_DATA) public data: { accountGroup: AccountGroup },
  ) {}

  ngOnInit(): void {
    this.accountGroup = this.data.accountGroup;
    this.loading = true;
    this.accountService.getUserAssociationsForDeletion(this.accountGroup.accountGroupId).subscribe({
      next: (response) => {
        this.loading = false;
        for (const userId in response) {
          if (Object.prototype.hasOwnProperty.call(response, userId)) {
            const userResponse = response[userId];
            const user = new User(userResponse.user_model);

            this.unassociatedUsers.push(user);
            if (userResponse.will_be_orphaned) {
              this.orphanedUsers.push(user);
            }
          }
        }
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  deleteAccount(): void {
    if (this.deleteLoading) {
      return;
    }
    this.deleteLoading = true;
    this.accountGroupSdkService.delete(this.accountGroup.accountGroupId).subscribe({
      next: () => {
        this.deleteLoading = false;
        this.snackbarService.openSuccessSnack(`Deleted ${this.accountGroup.napData.companyName}.`);
        this.accountGroupService.removeAccountGroupFromList(this.accountGroup.accountGroupId);
        this.dialogRef.close(true);
      },
      error: () => {
        this.deleteLoading = false;
        this.snackbarService.openErrorSnack(`Failed to delete ${this.accountGroup.napData.companyName}.`);
      },
    });
  }
}
